import React from 'react';

import { Divider, Grid, Skeleton, Typography } from '@mui/material';
import { AccountsRolesAutoComplete } from 'src/components/AccountsRolesAutoComplete';
import type {
  UserDefinedRoleIdToDetails,
  WorkflowAssignmentPermissions,
} from 'src/components/AssignRoleList';
import { useWorkflowRoleAssignments } from 'src/components/AssignRoleList';
import type { AssignRoleRowProps } from 'src/components/AssignRoleRow';
import { useAssignRoleRow } from 'src/components/AssignRoleRow';
import { makeStyles } from 'tss-react/mui';

import { StepLayout } from '../StepLayout';

const useStyles = makeStyles()(theme => ({
  assignAutocomplete: {
    maxWidth: 480,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export function AssignRoles({
  onValuesChange,
  roleAssignmentMap,
  workflowPermissions,
}: {
  onValuesChange: (roleAssignmentMap: UserDefinedRoleIdToDetails) => void;
  roleAssignmentMap: UserDefinedRoleIdToDetails;
  workflowPermissions: WorkflowAssignmentPermissions;
}) {
  const { classes } = useStyles();
  const { filteredRoleAssignments, getInitialValues, handleRowChange } = useWorkflowRoleAssignments(
    {
      filterBy: 'unassigned',
      onListChange: onValuesChange,
      roleAssignmentMap,
      workflowPermissions,
    }
  );

  if (!filteredRoleAssignments.length) return null;

  return (
    <StepLayout title="Assign roles">
      <div className={classes.cardContent}>
        <AssignRoleHeader />
        <Divider />
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          {filteredRoleAssignments.map(roleAssignment => {
            const item = roleAssignmentMap[roleAssignment.assigneeRole.id];
            const initialUserOrEmail = getInitialValues(item);

            return (
              <AssignRoleRow
                key={roleAssignment.assigneeRole.id}
                assignment={roleAssignment}
                initialUserOrEmail={initialUserOrEmail}
                onChange={handleRowChange}
              />
            );
          })}
        </Grid>
      </div>
    </StepLayout>
  );
}

function AssignRoleRow({ assignment, initialUserOrEmail, onChange }: AssignRoleRowProps) {
  const { classes } = useStyles();
  const { handleAutocompleteChange, userOrSuggested } = useAssignRoleRow({
    assignment,
    initialUserOrEmail,
    onChange,
  });

  return (
    <>
      <Grid size={3}>
        <Typography variant="body1">{assignment.assigneeRole.roleName || 'Untitled'}:</Typography>
      </Grid>
      <Grid size={9}>
        <AccountsRolesAutoComplete
          suggestInviteEmail
          className={classes.assignAutocomplete}
          includeOptions="users-contacts"
          placeholder="Search users and contacts, or type an email..."
          value={userOrSuggested}
          variant="avatar"
          onChange={handleAutocompleteChange}
        />
      </Grid>
    </>
  );
}

export function AssignRoleHeader(): JSX.Element {
  return (
    <Grid container spacing={2}>
      <Grid size={3}>
        <Typography sx={{ fontWeight: 600 }} variant="body1">
          Role
        </Typography>
      </Grid>
      <Grid size={9}>
        <Typography sx={{ fontWeight: 600 }} variant="body1">
          User or email
        </Typography>
      </Grid>
    </Grid>
  );
}

export function AssignRoleListLoading() {
  return (
    <>
      <AssignRoleHeader />
      <Divider />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid size={3}>
          <Typography variant="body1">
            <Skeleton height={16} variant="text" width={64} />
          </Typography>
        </Grid>
        <Grid size={9}>
          <Skeleton height={32} variant="rectangular" width={240} />
        </Grid>
      </Grid>
    </>
  );
}
